import { Link, notFound, useParams, useSearch } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { quoteQuery } from '@/queries';
import { ArrowLeft } from '@phosphor-icons/react';

import { ProofResponse } from '@/components/proof-response';
import { formatDate } from '@/components/status-timeline';
import { ButtonLink, Callout, EmptyState } from '@/components/ui';

export default function ProofPage() {
  const { ref } = useParams({ strict: false }) as { ref: string };
  // The token from the link. Without it this page is a 404 for the
  // customer who has never signed in, which is most of them (R-155).
  const { t: token } = useSearch({ strict: false }) as { t?: string };
  const { quote } = useSuspenseQuery(quoteQuery(ref, token)).data;
  if (!quote) throw notFound();

  const pending = quote.proofs.find((p) => p.status === 'pending');
  const latest = quote.proofs[quote.proofs.length - 1];

  return (
    <div className="mx-auto max-w-[900px] px-5 py-8">
      <Link
        to="/quotes/$ref" params={{ ref: quote.ref }}
        className="inline-flex items-center gap-2 py-2 text-sm font-semibold text-muted hover:text-ink"
      >
        <ArrowLeft size={16} weight="bold" aria-hidden />
        Quote {quote.ref}
      </Link>

      <h1 className="mt-4 text-[32px] font-semibold tracking-[-0.02em]">
        {pending ? `Proof round ${pending.round}` : 'Artwork proofs'}
      </h1>

      {!pending ? (
        <div className="mt-8">
          {latest ? (
            <Callout
              tone={latest.status === 'approved' ? 'ok' : 'neutral'}
              title={
                latest.status === 'approved'
                  ? `Round ${latest.round} is approved`
                  : `Round ${latest.round} has changes requested`
              }
            >
              {latest.status === 'approved'
                ? 'Nothing else is needed from you. We are getting it into production.'
                : 'We are working on the next round and will send it through shortly.'}
            </Callout>
          ) : (
            <EmptyState
              title="No proof yet"
              body="Once we have priced your quote we send an artwork proof to approve here."
              action={<ButtonLink href={`/quotes/${quote.ref}`}>Back to the quote</ButtonLink>}
            />
          )}
        </div>
      ) : (
        <>
          <p className="mt-2 text-[15px] text-muted">
            Sent {formatDate(pending.created_at)}. Approve it and we start production, or
            tell us what to change.
          </p>

          <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
            <div className="checkerboard rounded-[10px] border border-line p-6">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={pending.image_url}
                alt={`Artwork proof round ${pending.round} for quote ${quote.ref}`}
                className="mx-auto max-h-[520px] w-auto object-contain"
              />
            </div>

            <div className="space-y-6">
              {pending.note && (
                <div className="rounded-[10px] border border-line bg-panel p-5">
                  <h2 className="text-sm font-bold">Our note</h2>
                  <p className="mt-2 text-[15px] leading-relaxed">{pending.note}</p>
                </div>
              )}
              <ProofResponse proofId={pending.id} quoteRef={quote.ref} round={pending.round} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
